// components/SearchBar.js
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWithAuth } from '../auth';

export default function SearchBar({ className = '' }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [error, setError] = useState('');
  
  const abortControllerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Debounced search
  useEffect(() => {
    if (query.length < 2) {
      setResults(null);
      setIsOpen(false);
      setLoading(false);
      return;
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setLoading(true);
    setError('');

    const timeoutId = setTimeout(async () => {
      try {
        const res = await fetchWithAuth(`/api/search/?q=${encodeURIComponent(query)}`, {
          signal,
        });
        
        if (!res.ok) {
          if (!signal.aborted) {
            throw new Error('Search failed');
          }
          return;
        }
        
        const data = await res.json();
        
        if (!signal.aborted) {
          setResults(data);
          setIsOpen(true);
          setSelectedIndex(-1);
        }
      } catch (err) {
        if (err.name !== 'AbortError' && !signal.aborted) {
          setError('שגיאה בחיפוש');
          setResults(null);
        }
      } finally {
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    }, 300); // 300ms debounce

    return () => {
      clearTimeout(timeoutId);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [query]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen || !results) return;

    const allResults = [
      ...results.disciplines,
      ...results.books,
      ...results.topics,
      ...results.resources,
      ...results.files,
      ...results.projects,
    ];

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => {
          const newIndex = prev < allResults.length - 1 ? prev + 1 : prev;
          // Scroll selected item into view
          setTimeout(() => {
            const selectedElement = dropdownRef.current?.querySelector(`[data-index="${newIndex}"]`);
            selectedElement?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }, 0);
          return newIndex;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => {
          const newIndex = prev > 0 ? prev - 1 : -1;
          // Scroll selected item into view
          setTimeout(() => {
            const selectedElement = dropdownRef.current?.querySelector(`[data-index="${newIndex}"]`);
            selectedElement?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }, 0);
          return newIndex;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && allResults[selectedIndex]) {
          handleResultClick(allResults[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setQuery('');
        break;
    }
  };

  const handleResultClick = (result) => {
    navigate(result.url);
    setIsOpen(false);
    setQuery('');
    setSelectedIndex(-1);
  };

  const hasResults = results && (
    results.disciplines.length > 0 ||
    results.books.length > 0 ||
    results.topics.length > 0 ||
    results.resources.length > 0 ||
    results.files.length > 0 ||
    results.projects.length > 0
  );

  const allResults = results ? [
    ...results.disciplines,
    ...results.books,
    ...results.topics,
    ...results.resources,
    ...results.files,
    ...results.projects,
  ] : [];

  return (
    <div className={`relative w-full max-w-2xl mx-auto ${className}`}>
      <div className="relative z-10">
        <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none z-10">
          {loading ? (
            <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          ) : (
            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          )}
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (hasResults) setIsOpen(true);
          }}
          placeholder="חפש בכל האתר..."
          className="w-full h-14 pl-4 pr-12 text-right bg-white dark:bg-white text-gray-800 rounded-full shadow-lg focus:outline-none focus:ring-4 focus:ring-blue-300 transition-all text-lg placeholder-gray-400 relative z-10"
          dir="rtl"
        />
      </div>

      {/* Dropdown Results */}
      {isOpen && query.length >= 2 && (
        <div
          ref={dropdownRef}
          className="absolute z-[9999] w-full mt-2 bg-white dark:bg-secondary-800 rounded-lg shadow-xl border border-secondary-200 dark:border-secondary-700 max-h-96 overflow-y-auto"
        >
          {loading ? (
            <div className="p-4 text-center text-secondary-500 dark:text-secondary-400">
              מחפש...
            </div>
          ) : error ? (
            <div className="p-4 text-center text-red-600 dark:text-red-400">
              {error}
            </div>
          ) : !hasResults ? (
            <div className="p-4 text-center text-secondary-500 dark:text-secondary-400">
              לא נמצאו תוצאות
            </div>
          ) : (
            <div className="py-2">
              {results.disciplines.length > 0 && (
                <ResultSection
                  title="תחומי דעת"
                  items={results.disciplines}
                  selectedIndex={selectedIndex}
                  allResults={allResults}
                  onItemClick={handleResultClick}
                />
              )}
              {results.books.length > 0 && (
                <ResultSection
                  title="ספרים"
                  items={results.books}
                  selectedIndex={selectedIndex}
                  allResults={allResults}
                  onItemClick={handleResultClick}
                />
              )}
              {results.topics.length > 0 && (
                <ResultSection
                  title="נושאים"
                  items={results.topics}
                  selectedIndex={selectedIndex}
                  allResults={allResults}
                  onItemClick={handleResultClick}
                />
              )}
              {results.projects.length > 0 && (
                <ResultSection
                  title="פרויקטים"
                  items={results.projects}
                  selectedIndex={selectedIndex}
                  allResults={allResults}
                  onItemClick={handleResultClick}
                />
              )}
              {results.resources.length > 0 && (
                <ResultSection
                  title="משאבים"
                  items={results.resources}
                  selectedIndex={selectedIndex}
                  allResults={allResults}
                  onItemClick={handleResultClick}
                />
              )}
              {results.files.length > 0 && (
                <ResultSection
                  title="קבצים"
                  items={results.files}
                  selectedIndex={selectedIndex}
                  allResults={allResults}
                  onItemClick={handleResultClick}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ResultSection({ title, items, selectedIndex, allResults, onItemClick }) {
  return (
    <div>
      <div className="px-4 py-2 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wide border-b border-secondary-200 dark:border-secondary-700">
        {title}
      </div>
      {items.map((item, index) => {
        const globalIndex = allResults.indexOf(item);
        const isSelected = globalIndex === selectedIndex;
        
        return (
          <button
            key={`${item.type}-${item.id}`}
            data-index={globalIndex}
            onClick={() => onItemClick(item)}
            className={`w-full text-right px-4 py-3 hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors ${
              isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : ''
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-secondary-900 dark:text-white truncate">
                  {item.name}
                </div>
                {item.breadcrumb && (
                  <div className="text-sm text-secondary-500 dark:text-secondary-400 truncate mt-1">
                    {item.breadcrumb}
                  </div>
                )}
              </div>
              <div className="flex-shrink-0">
                <ResultTypeIcon type={item.type} />
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function ResultTypeIcon({ type }) {
  const icons = {
    discipline: (
      <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    book: (
      <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    ),
    topic: (
      <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
      </svg>
    ),
    resource: (
      <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    file: (
      <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    project: (
      <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    ),
  };

  return icons[type] || icons.resource;
}

